import  { useState, useEffect, useRef, useCallback } from "react";
import "./FruitCatcher.css";

const FruitCatcher = ({ onBack, isPaused }) => {
    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(3);
    const [gameOver, setGameOver] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const canvasRef = useRef(null);

    const game = useRef({
        basket: { x: 200, y: 400, w: 95, h: 32 },
        fruits: [],
        lastFrame: 0,
        spawnTimer: 0
    });

    const fruitsList = ["🍎", "🍊", "🍇", "🍉", "🍓", "🍌"];

    const handleMove = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        
        let clientX;
        if (e.touches && e.touches.length > 0) {
            clientX = e.touches[0].clientX;
        } else if (e.clientX !== undefined) {
            clientX = e.clientX;
        } else {
            return;
        }

        let x = clientX - rect.left;
        const scaleX = canvas.width / rect.width;
        x = x * scaleX;

        const basket = game.current.basket;
        x = Math.max(basket.w/2, Math.min(canvas.width - basket.w/2, x));
        basket.x = x;
    };

    const startGame = () => {
        setScore(0);
        setLives(3);
        setGameOver(false);
        setGameStarted(true);
        game.current.fruits = [];
        game.current.spawnTimer = 0;
        game.current.lastFrame = performance.now();
        
        const canvas = canvasRef.current;
        if (canvas) {
            canvas.width = canvas.clientWidth;
            canvas.height = canvas.clientHeight;
            game.current.basket.x = canvas.width / 2;
            game.current.basket.y = canvas.height - 80;
        }
    };

    const update = useCallback((time) => {
        if (!gameStarted || gameOver) return;
        if (isPaused) {
            game.current.lastFrame = time;
            render();
            requestAnimationFrame(update);
            return;
        }
        
        let dt = (time - game.current.lastFrame) / 16;
        if (dt > 3) dt = 3;
        if (dt < 0) dt = 0;
        
        game.current.lastFrame = time;
        const g = game.current;
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Dynamic resolution sync
        const displayWidth = canvas.clientWidth;
        const displayHeight = canvas.clientHeight;
        if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
            canvas.width = displayWidth;
            canvas.height = displayHeight;
            g.basket.y = canvas.height - 80;
        }

        const CAN_W = canvas.width;
        const CAN_H = canvas.height;

        // Spawn fruits
        g.spawnTimer += dt;
        const spawnInterval = Math.max(25, 75 - score * 0.4); // Adaptive progression
        if (g.spawnTimer > spawnInterval) {
            g.spawnTimer = 0;
            g.fruits.push({
                x: Math.random() * (CAN_W - 60) + 30,
                y: -40,
                speed: 3 + Math.random() * 3 + score * 0.04,
                type: fruitsList[Math.floor(Math.random() * fruitsList.length)]
            });
        }

        // Update fruits
        for (let i = g.fruits.length - 1; i >= 0; i--) {
            let f = g.fruits[i];
            f.y += f.speed * dt;

            // Catch check
            const basket = g.basket;
            if (f.y + 15 > basket.y && f.y < basket.y + basket.h) {
                if (Math.abs(f.x - basket.x) < basket.w / 2 + 15) {
                    setScore(s => s + 10);
                    g.fruits.splice(i, 1);
                    continue;
                }
            }

            // Miss check
            if (f.y > CAN_H + 20) {
                g.fruits.splice(i, 1);
                setLives(l => {
                    const newLives = l - 1;
                    if (newLives <= 0) {
                        setGameOver(true);
                    }
                    return newLives;
                });
            }
        }

        render();
        requestAnimationFrame(update);
    }, [gameStarted, gameOver, score, isPaused]);

    const render = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const CAN_W = canvas.width;
        const CAN_H = canvas.height;
        
        ctx.clearRect(0, 0, CAN_W, CAN_H);

        // Sky background gradient
        const grad = ctx.createLinearGradient(0, 0, 0, CAN_H);
        grad.addColorStop(0, "#0ea5e9"); // Deep sky blue
        grad.addColorStop(1, "#bae6fd"); // Light soft blue
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, CAN_W, CAN_H);

        // Clouds (Scaled dynamically to screen width/height)
        ctx.fillStyle = "rgba(255, 255, 255, 0.45)";
        
        // Cloud 1
        ctx.beginPath();
        ctx.arc(CAN_W * 0.25, CAN_H * 0.2, 35, 0, Math.PI * 2);
        ctx.arc(CAN_W * 0.35, CAN_H * 0.18, 45, 0, Math.PI * 2);
        ctx.arc(CAN_W * 0.45, CAN_H * 0.2, 35, 0, Math.PI * 2);
        ctx.fill();

        // Cloud 2
        ctx.beginPath();
        ctx.arc(CAN_W * 0.7, CAN_H * 0.35, 30, 0, Math.PI * 2);
        ctx.arc(CAN_W * 0.8, CAN_H * 0.33, 40, 0, Math.PI * 2);
        ctx.arc(CAN_W * 0.9, CAN_H * 0.35, 30, 0, Math.PI * 2);
        ctx.fill();

        // Cloud 3
        ctx.beginPath();
        ctx.arc(CAN_W * 0.1, CAN_H * 0.6, 20, 0, Math.PI * 2);
        ctx.arc(CAN_W * 0.18, CAN_H * 0.58, 30, 0, Math.PI * 2);
        ctx.arc(CAN_W * 0.26, CAN_H * 0.6, 20, 0, Math.PI * 2);
        ctx.fill();

        // Cloud 4 (on wider screens)
        if (CAN_W > 600) {
            ctx.beginPath();
            ctx.arc(CAN_W * 0.5, CAN_H * 0.5, 25, 0, Math.PI * 2);
            ctx.arc(CAN_W * 0.56, CAN_H * 0.48, 35, 0, Math.PI * 2);
            ctx.arc(CAN_W * 0.62, CAN_H * 0.5, 25, 0, Math.PI * 2);
            ctx.fill();
        }

        // Draw Basket
        const basket = game.current.basket;
        
        ctx.shadowColor = "rgba(0,0,0,0.3)";
        ctx.shadowBlur = 12;
        ctx.shadowOffsetY = 6;

        // Basket body
        ctx.fillStyle = "#854d0e"; 
        ctx.beginPath();
        ctx.moveTo(basket.x - basket.w/2, basket.y);
        ctx.lineTo(basket.x + basket.w/2, basket.y);
        ctx.lineTo(basket.x + basket.w/2 - 12, basket.y + basket.h);
        ctx.lineTo(basket.x - basket.w/2 + 12, basket.y + basket.h);
        ctx.closePath();
        ctx.fill();

        ctx.shadowColor = "transparent";
        
        // Rim
        ctx.fillStyle = "#eab308";
        ctx.beginPath();
        ctx.roundRect(basket.x - basket.w/2 - 5, basket.y - 6, basket.w + 10, 10, 5);
        ctx.fill();

        ctx.fillStyle = "#fef08a";
        ctx.beginPath();
        ctx.roundRect(basket.x - basket.w/2 - 5, basket.y - 6, basket.w + 10, 3, 2);
        ctx.fill();

        // Weave lines
        ctx.strokeStyle = "#713f12";
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(basket.x - basket.w/2 + 10, basket.y + 11);
        ctx.lineTo(basket.x + basket.w/2 - 10, basket.y + 11);
        ctx.moveTo(basket.x - basket.w/2 + 14, basket.y + 21);
        ctx.lineTo(basket.x + basket.w/2 - 14, basket.y + 21);
        ctx.stroke();

        // Draw Emojis
        ctx.shadowColor = "rgba(0,0,0,0.2)";
        ctx.shadowBlur = 8;
        ctx.shadowOffsetY = 4;
        ctx.font = "40px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        game.current.fruits.forEach(f => {
            ctx.fillText(f.type, f.x, f.y);
        });
        ctx.shadowColor = "transparent";
    };

    useEffect(() => {
        let animationId;
        if (gameStarted && !gameOver) {
            game.current.lastFrame = performance.now();
            animationId = requestAnimationFrame(update);
        } else {
            const canvas = canvasRef.current;
            if (canvas) {
                canvas.width = canvas.clientWidth;
                canvas.height = canvas.clientHeight;
                game.current.basket.x = canvas.width / 2;
                game.current.basket.y = canvas.height - 80;
            }
            render();
        }
        return () => cancelAnimationFrame(animationId);
    }, [gameStarted, gameOver, update]);

    useEffect(() => {
        const handleResize = () => {
            const canvas = canvasRef.current;
            if (canvas) {
                canvas.width = canvas.clientWidth;
                canvas.height = canvas.clientHeight;
                if (!gameStarted) {
                    game.current.basket.x = canvas.width / 2;
                    game.current.basket.y = canvas.height - 80;
                }
                render();
            }
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, [gameStarted]);

    return (
        <div className="fc_container" onMouseMove={handleMove} onTouchMove={(e) => { e.preventDefault(); handleMove(e); }}>
            <div className="fc_header">
                <button onClick={(e) => { e.stopPropagation(); onBack(); }} className="fc_back">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                    Hub
                </button>
                <div className="fc_score_container">
                    <div className="fc_score">SCORE: {score}</div>
                    <div className="fc_lives">{"✨".repeat(Math.max(0, lives))}</div>
                </div>
            </div>
            
            <div className="fc_canvas_wrapper" onTouchStart={(e) => { if(gameStarted) handleMove(e); }}>
                <canvas ref={canvasRef} />
                
                {!gameStarted && !gameOver && (
                    <div className="fc_overlay">
                        <div className="fc_overlay_card">
                            <div className="fc_icon_bounce">🍎</div>
                            <h1>FRUIT <span>CATCHER</span></h1>
                            <p>Drag the basket to catch<br/>the falling fruits!</p>
                            <button className="fc_btn_primary" onClick={(e) => { e.stopPropagation(); startGame(); }}>PLAY NOW</button>
                        </div>
                    </div>
                )}
                {gameOver && (
                    <div className="fc_overlay">
                        <div className="fc_overlay_card">
                            <div className="fc_icon_shake">💥</div>
                            <h1>GAME <span>OVER</span></h1>
                            <p>You scored <strong>{score}</strong> points!</p>
                            <button className="fc_btn_primary" onClick={(e) => { e.stopPropagation(); startGame(); }}>PLAY AGAIN</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FruitCatcher;
